'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PlayIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useTriggerBackup } from '@/hooks'

type PropsType = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const TriggerBackupDialog = ({ open, onOpenChange }: PropsType) => {
  const t = useTranslations()
  const queryClient = useQueryClient()
  const [includeDatabase, setIncludeDatabase] = useState(true)
  const [includeStorage, setIncludeStorage] = useState(true)
  const { mutate: triggerBackup, isPending } = useTriggerBackup()

  const handleTrigger = () => {
    triggerBackup(
      { includeDatabase, includeStorage },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['backups'] })
          toast.success(t('backup_triggered'))
          onOpenChange(false)
        },
        onError: () => {
          toast.error(t('error_creating'))
        }
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('trigger_backup')}</DialogTitle>
          <DialogDescription>{t('trigger_backup_confirm')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="includeDatabase"
              checked={includeDatabase}
              onCheckedChange={checked => setIncludeDatabase(!!checked)}
            />
            <Label htmlFor="includeDatabase">{t('include_database')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="includeStorage"
              checked={includeStorage}
              onCheckedChange={checked => setIncludeStorage(!!checked)}
            />
            <Label htmlFor="includeStorage">{t('include_storage')}</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={handleTrigger} disabled={isPending || (!includeDatabase && !includeStorage)}>
            <PlayIcon className="h-4 w-4" />
            {isPending ? t('running_backup') : t('trigger_backup')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TriggerBackupDialog
